import {  PayloadAction, createSlice } from '@reduxjs/toolkit'
import { SLICE_KEYS } from '../SliceKeys'
import { IAppReadyAction, IApplicationState, IKontomatikBankVerificationAction } from './ApplicationSliceModels'
import { getCredentials, tryReconnectToHub } from './middleware/credential.action'
import { DateTime } from 'luxon'
import { IGetCredentialsThunkActionReturnProps } from './middleware/credential-actions-models'
import { PAGE_STEPER_STAGES, PAGE_STEPPER_MODES, allPageStepsModes } from 'components/Navbar/pageSteps'
import { WorkflowTypes } from 'common/workflow'
import { IRecalculatePreOffertResponse } from 'service/PreOfferService/PreOfferServiceProps'


export const initialApplicationState: IApplicationState = {
  offert: {
    ckdz: 0,
    instalmentAmount: 0,
    instalments: 0,
    loanAmountGross: 0,
    interestRate: 0,
    loanAmountNet: 0,
    rrso: 0,
    minimalCommisionAmount: 0,
    mininalAmountToPay: 0,
    paymentDate: new Date().toISOString(),
    paymentSchedule: [],
    earlyRRSO: 0,
  },
  currentPageStepperStatus: {
    currentStep: undefined,
    activeSteps: []
  },
  isLoading: true,
  billingCompany: "",
  appId: "",
  amountToPay: 0,
  isOffline: false,
  wasOffline : false,
  successReturnUrl: "",
  failureReturnUrl: "",
  waitingScreenText: undefined,
  kontomatikWidgetClient: "",
  kontomatikWidgetOwnerExternalId: "",
  applicationStatus: "",
  workflowId: null,
  hashedPhoneNumber: undefined,
  maxLoanPeriod: null,
  isAdditionalVerificationMethodsAvailable: false,
  alphaId: null,

  currentPageStepperMode: PAGE_STEPPER_MODES.DEFAULT,
}

export const applicationSlice = createSlice({
  name: SLICE_KEYS.APPLICATION_SLICE,
  initialState: initialApplicationState,
  reducers: {
    appBackOnline: (state) => {
      state.wasOffline = false
    },
    appOffline: (state) => {
      state.isOffline = true
      state.wasOffline = true
    },
    appOnline: (state) => {
      state.isOffline = false
    },
    changeWaitingScreenText: (state, action: PayloadAction<string>) => {
      state.waitingScreenText = action.payload
    },
    appReady: (state, action: PayloadAction<IAppReadyAction>) => {
      state.offert = action.payload.offert
      state.billingCompany = action.payload.billingCompany
      state.appId = action.payload.appId
      state.amountToPay = action.payload.amountToPay
      state.isLoading = false
      state.successReturnUrl = action.payload.successReturnUrl ?? ""
      state.failureReturnUrl = action.payload.failureReturnUrl ?? ""
      state.applicationStatus = action.payload.applicationStatus ?? ""
      state.workflowId = action.payload.workflowId ?? null
    },
    clear: (state) => {
      state = initialApplicationState
    },
    changeApplicationStep: (state, action: PayloadAction<PAGE_STEPER_STAGES>) => {
      const newActiveStepsArray = state.currentPageStepperStatus.activeSteps
      const newActiveStep = allPageStepsModes[state.currentPageStepperMode][action.payload]

      if(newActiveStep !== undefined) {
        newActiveStepsArray.push(newActiveStep)
      }

      state.currentPageStepperStatus.activeSteps = newActiveStepsArray
      state.currentPageStepperStatus.currentStep = action.payload
    },
    kontomatikBankVerification: (state, action: PayloadAction<IKontomatikBankVerificationAction>) => {
      state.kontomatikWidgetClient = action.payload.kontomatikWidgetClient
      state.kontomatikWidgetOwnerExternalId = action.payload.kontomatikWidgetOwnerExternalId
    },
    changeWorkflow: (state, action: PayloadAction<WorkflowTypes>) => {
      state.workflowId = (action.payload) as number
    },
    setCurrentStepperMode: (state, action: PayloadAction<PAGE_STEPPER_MODES>) => {
      state.currentPageStepperMode = action.payload
    },
    updateOffertOnCalendarSelectionChange: (state, action: PayloadAction<IRecalculatePreOffertResponse>) => {
      state.offert.ckdz = action.payload.ckdz
      state.offert.earlyRRSO = action.payload.earlyRRSO
      state.offert.loanAmountGross = action.payload.loanAmountGross
      state.offert.loanAmountNet = action.payload.loanAmountNet
      state.offert.instalments = action.payload.instalments
      state.offert.interestRate = action.payload.interestRate
      state.offert.instalmentAmount = action.payload.instalmentAmount
      state.offert.minimalCommisionAmount = action.payload.minimalCommisionAmount
      state.offert.mininalAmountToPay = action.payload.mininalAmountToPay
      state.offert.paymentDate = action.payload.paymentDate
      state.offert.rrso = action.payload.rrso
      state.offert.paymentSchedule = action.payload.paymentSchedule
    },
    setNewFailureReturnUrl: (state, action: PayloadAction<string>) => {
      state.failureReturnUrl = action.payload
    },
    setIsAdditionalVerificationMethodsAvailable: (state, action: PayloadAction<boolean>) => {
      state.isAdditionalVerificationMethodsAvailable = action.payload
    },
    setAlphaId: (state, action: PayloadAction<string>) => {
      state.alphaId = action.payload
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getCredentials.fulfilled, (state, action: PayloadAction<IGetCredentialsThunkActionReturnProps | undefined>) => {
      if(action.payload !== undefined){
        state.offert = action.payload.offert
        state.billingCompany = action.payload.billingCompany
        state.appId = action.payload.appId
        state.amountToPay = action.payload.amountToPay
        state.isLoading = false
        state.successReturnUrl = action.payload.successReturnUrl ?? ""
        state.failureReturnUrl = action.payload.failureReturnUrl ?? ""
        state.applicationStatus = action.payload.applicationStatus ?? ""
        state.workflowId = action.payload.workflowId ?? null
        state.hashedPhoneNumber = action.payload.hashedPhoneNumber
        state.maxLoanPeriod = action.payload.maxLoanPeriod
      }
    })
    builder.addCase(tryReconnectToHub.fulfilled, (state, action) => {})
  }  
})

// Action creators are generated for each case reducer function
export const { 
  appBackOnline, 
  appOffline, 
  appOnline, 
  appReady, 
  clear, 
  changeWaitingScreenText,
  changeApplicationStep, 
  kontomatikBankVerification, 
  changeWorkflow, 
  setCurrentStepperMode,
  updateOffertOnCalendarSelectionChange,
  setNewFailureReturnUrl,
  setIsAdditionalVerificationMethodsAvailable,
  setAlphaId,
} = applicationSlice.actions

export default applicationSlice.reducer